import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import type { PriceLineItemProps } from "./PriceLineItem.types";
import { PriceLineItemLabel } from "./PriceLineItemLabel";
import { PriceLineItemLeftOffset } from "./PriceLineItemLeftOffset";

const primaryTypefaceVariants = cva("", {
  variants: {
    size: {
      small: "",
      medium: "",
      large: "",
    },
    emphasis: {
      default: "",
      strong: "",
    },
  },
  compoundVariants: [
    { size: "small", emphasis: "default", class: "tw-body-1" },
    { size: "small", emphasis: "strong", class: "tw-body-1-m" },
    { size: "medium", emphasis: "default", class: "tw-body-2" },
    { size: "medium", emphasis: "strong", class: "tw-body-2-m" },
    { size: "large", emphasis: "default", class: "tw-body-3" },
    { size: "large", emphasis: "strong", class: "tw-body-3 tw-font-medium" },
  ],
  defaultVariants: {
    size: "medium",
    emphasis: "default",
  },
});

const primaryColorVariants = cva("", {
  variants: {
    color: {
      default: "tw-text-gray-900",
      highlight: "tw-text-purple-800",
    },
  },
  defaultVariants: {
    color: "default",
  },
});

const captionColorVariants = cva("", {
  variants: {
    color: {
      default: "tw-text-gray-600",
      highlight: "tw-text-purple-800",
    },
  },
  defaultVariants: {
    color: "default",
  },
});

/**
 * Presentational price line item with fill / optional 40px middle / value tracks.
 * The value track is content-sized so narrow containers spend their width on
 * the label; values still align because every row shares the same right edge.
 */
const PriceLineItem = ({
  caption,
  captionValue,
  className,
  color = "default",
  emphasis = "default",
  label,
  leftOffset = "none",
  middleContent,
  size = "medium",
  testId,
  tooltip,
  value,
}: PriceLineItemProps) => {
  const typefaceClass = primaryTypefaceVariants({ size, emphasis });
  const primaryColorClass = primaryColorVariants({ color });
  const captionColorClass = captionColorVariants({ color });

  return (
    <div
      className={cn("tw-flex tw-items-start tw-gap-8 tw-w-full", className)}
      data-testid={testId}
    >
      {leftOffset !== "none" && (
        <PriceLineItemLeftOffset leftOffset={leftOffset} size={size} />
      )}
      <div
        className={cn("tw-grid tw-gap-16 tw-min-w-0 tw-flex-1 tw-w-full", {
          "tw-grid-cols-[minmax(0,1fr)_auto]": !middleContent,
          "tw-grid-cols-[minmax(0,1fr)_40px_auto]": !!middleContent,
        })}
      >
        <div className="tw-min-w-0">
          <PriceLineItemLabel
            colorClass={primaryColorClass}
            label={label}
            testId={testId}
            tooltip={tooltip}
            typefaceClass={typefaceClass}
          />
          {caption && (
            <div
              className={cn("tw-caption-2", captionColorClass)}
              data-testid={testId ? `${testId}-caption` : undefined}
            >
              {caption}
            </div>
          )}
        </div>

        {middleContent && (
          <div
            className={cn(
              "tw-w-40 tw-text-left",
              typefaceClass,
              primaryColorClass,
            )}
            data-testid={testId ? `${testId}-middle` : undefined}
          >
            {middleContent}
          </div>
        )}

        <div className="tw-text-right">
          <div
            className={cn(
              "tw-whitespace-nowrap",
              typefaceClass,
              primaryColorClass,
            )}
            data-testid={testId ? `${testId}-value` : undefined}
          >
            {value}
          </div>
          {captionValue && (
            <div
              className={cn("tw-caption-2", captionColorClass)}
              data-testid={testId ? `${testId}-caption-value` : undefined}
            >
              {captionValue}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PriceLineItem.displayName = "PriceLineItem";

export { PriceLineItem };
